// ─── KAELO Configuration ─────────────────────────────────────────────────────
// All office/agent definitions live here.
// To add a new office: add entry to OFFICES array with id, name, color, dockApps.
// To add a new agent: add entry to STATIC_AGENTS array.

import { OfficeConfig, OfficeId, OfficePanelId, HQPanelId } from './types';

// ─── Office Definitions ───────────────────────────────────────────────────────

export const OFFICES: OfficeConfig[] = [
  {
    id: 'home',
    name: 'Home',
    color: '#F5C542',
    description: 'Personal & family life management',
    hqApps: ['chat', 'kanban', 'gantt', 'team', 'calendar', 'budget'],
  },
  {
    id: 'kaalvoet',
    name: 'Kaalvoet',
    color: '#9B59B6',
    description: 'Architecture studio — design to delivery',
    hqApps: ['chat', 'kanban', 'gantt', 'team', 'projects', 'sacap'],
  },
  {
    id: 'zikhethele',
    name: 'Zikhethele',
    color: '#1ABC9C',
    description: 'Property practice — mandate to transfer',
    hqApps: ['chat', 'kanban', 'gantt', 'team', 'deals', 'leads'],
  },
  {
    id: 'developments',
    name: 'Developments',
    color: '#E74C3C',
    description: 'Property development pipeline',
    hqApps: ['chat', 'kanban', 'gantt', 'team', 'hopper', 'feasibility', 'windsor'],
  },
  {
    id: 'odimo',
    name: 'Odimo',
    color: '#3498DB',
    description: 'Rental management — short to long-term',
    hqApps: ['chat', 'kanban', 'gantt', 'team', 'properties', 'tenants', 'compliance'],
  },
];

// ─── HQ Dock App Definitions ─────────────────────────────────────────────────

export interface DockApp {
  id: HQPanelId;
  label: string;
  icon: string;
}

export const HQ_DOCK_APPS: DockApp[] = [
  { id: 'chat', label: 'Chat', icon: 'MessageSquare' },
  { id: 'offices', label: 'Offices', icon: 'Building2' },
  { id: 'kanban', label: 'Kanban', icon: 'Columns3' },
  { id: 'gantt', label: 'Timeline', icon: 'BarChart2' },
  { id: 'calendar', label: 'Calendar', icon: 'Calendar' },
  { id: 'reports', label: 'Reports', icon: 'FileText' },
  { id: 'registry', label: 'Registry', icon: 'Database' },
  { id: 'notifications', label: 'Alerts', icon: 'Bell' },
  { id: 'settings', label: 'Settings', icon: 'Settings' },
  { id: 'users', label: 'Users', icon: 'Users' },
];

// ─── Office Dock App Labels ───────────────────────────────────────────────────

export const OFFICE_PANEL_LABELS: Record<OfficePanelId, string> = {
  chat: 'Chat',
  kanban: 'Kanban',
  gantt: 'Timeline',
  team: 'Team',
  calendar: 'Calendar',
  budget: 'Budget',
  projects: 'Projects',
  sacap: 'SACAP',
  deals: 'Deal Pipeline',
  leads: 'Leads',
  hopper: 'Hopper',
  feasibility: 'Feasibility',
  windsor: 'Windsor Report',
  properties: 'Properties',
  tenants: 'Tenants',
  compliance: 'Compliance',
};

export const OFFICE_PANEL_ICONS: Record<OfficePanelId, string> = {
  chat: 'MessageSquare',
  kanban: 'Columns3',
  gantt: 'BarChart2',
  team: 'Users',
  calendar: 'Calendar',
  budget: 'DollarSign',
  projects: 'FolderOpen',
  sacap: 'Award',
  deals: 'TrendingUp',
  leads: 'Target',
  hopper: 'Layers',
  feasibility: 'Calculator',
  windsor: 'FileText',
  properties: 'Home',
  tenants: 'UserCheck',
  compliance: 'ShieldCheck',
};

// ─── Static Agent Fallbacks ───────────────────────────────────────────────────
// Used when GitHub API is unavailable or agent not found in repo.

export const STATIC_AGENTS = [
  {
    name: 'KAELO',
    type: 'orchestrator',
    model: 'sonnet',
    description: 'Central orchestrator. Routes strategic decisions, manages escalations.',
    skills: ['strategy', 'orchestration', 'market-research'],
    officeId: undefined,
    status: 'active',
  },
  {
    name: 'Home',
    type: 'personal_constraint_calendar',
    model: 'haiku',
    description: 'Personal constraint calendar. Protects family time, manages personal obligations.',
    skills: ['content-engine', 'knowledge-ops', 'email-ops'],
    officeId: 'home' as OfficeId,
    status: 'active',
  },
  {
    name: 'Kaalvoet',
    type: 'architecture_studio',
    model: 'sonnet',
    description: 'Architecture studio agent. Manages projects from brief to archival.',
    skills: ['hexagonal-architecture', 'api-design', 'blueprint'],
    officeId: 'kaalvoet' as OfficeId,
    status: 'active',
  },
  {
    name: 'Zikhethele',
    type: 'property_practice',
    model: 'haiku',
    description: 'Property practice agent. Manages deal flow from mandate to transfer.',
    skills: ['data-scraper-agent', 'market-research', 'deep-research'],
    officeId: 'zikhethele' as OfficeId,
    status: 'active',
  },
  {
    name: 'Developments',
    type: 'development_pipeline',
    model: 'sonnet',
    description: 'Property development pipeline manager.',
    skills: ['project-flow-ops', 'feasibility', 'zoning'],
    officeId: 'developments' as OfficeId,
    status: 'idle',
  },
  {
    name: 'Odimo',
    type: 'rental_management',
    model: 'sonnet',
    description: 'Rental management platform — short to long-term, compliance-heavy.',
    skills: ['tenant-ops', 'compliance', 'lease-management'],
    officeId: 'odimo' as OfficeId,
    status: 'idle',
  },
];

// ─── Helper: Get office by id ─────────────────────────────────────────────────

export function getOffice(officeId: string): OfficeConfig | undefined {
  return OFFICES.find((o) => o.id === officeId);
}

// ─── Kanban Column Definitions ────────────────────────────────────────────────

export const KANBAN_COLUMNS = [
  { id: 'todo', label: 'To Do' },
  { id: 'inprogress', label: 'In Progress' },
  { id: 'review', label: 'Review' },
  { id: 'done', label: 'Done' },
] as const;
